import { useEffect, useRef } from "react";
import { UseFormReset } from "react-hook-form";
import {
  CampaignType,
  IMechanicConfig,
  ISlotSymbol,
  VoucherValidityMode,
} from "../../../../../@types/gamification";
import { STORE_CODE_VALUES } from "../../constants/store-options";
import { getDefaultFaqItems } from "../../constants/mechanic-defaults";
import { priorityLevelOf } from "../../constants/priority-levels";
import { type UpdateGamificationFormData } from "../../validations/gamification.validation";

function toSlotSymbol(symbol: ISlotSymbol & { icone?: string }): ISlotSymbol {
  const { icone, ...rest } = symbol;
  const icon = rest.icon ?? icone;
  return { ...rest, ...(icon ? { icon } : {}) };
}

interface Campaign {
  id: number;
  nickname: string;
  slug?: string;
  startDate?: string;
  endDate?: string;
  campaignType?: CampaignType;
  mechanicConfig?: IMechanicConfig;
  faqItems?: Array<{
    question: string;
    answer: string;
  }>;
  allStoresSelected?: boolean;
  stores?: Array<{
    storeCode: string;
    storeName?: string;
    nickname?: string;
  }>;
}

interface UseFormInitializationProps {
  campaign: Campaign | undefined;
  reset: UseFormReset<UpdateGamificationFormData>;
}

function toFormMechanicConfig(
  mc: IMechanicConfig | undefined,
) {
  const prizes = mc?.prizes
      ? mc.prizes.map((prize) => ({
          value: prize.value,
          probability: prize.probability,
          ...(prize.label ? { label: prize.label } : {}),
        }))
      : [];

  return {
    prizes,
    maxPlays: mc?.maxPlays,
    minPurchase: mc?.minPurchase,
    priority: priorityLevelOf(mc?.priority),
    concurrency: {
      competes: mc?.concurrency?.competes ?? false,
      group: mc?.concurrency?.group ?? "",
    },
    voucherValidity: {
      mode: mc?.voucherValidity?.mode ?? VoucherValidityMode.DAYS,
      validityDays: mc?.voucherValidity?.validityDays,
      startOffsetDays: mc?.voucherValidity?.startOffsetDays ?? 0,
    },
    cpfSegmentation: {
      enabled: mc?.cpfSegmentation?.enabled ?? false,
    },
    mechanicParams: {
      goalProbability: mc?.mechanicParams?.goalProbability,
      symbols: (mc?.mechanicParams?.symbols ?? []).map(toSlotSymbol),
    },
    presentation: {
      customerFacingName: mc?.presentation?.customerFacingName ?? "",
      customerFacingDescription:
        mc?.presentation?.customerFacingDescription ?? "",
    },
  };
}

export const useFormInitialization = ({
  campaign,
  reset,
}: UseFormInitializationProps) => {

  const initializedId = useRef<number | null>(null);

  useEffect(() => {
    if (!campaign) return;
    if (initializedId.current === campaign.id) return;
    initializedId.current = campaign.id;

    const formattedStartDate = campaign.startDate
      ? campaign.startDate.split("T")[0]
      : "";
    const formattedEndDate = campaign.endDate
      ? campaign.endDate.split("T")[0]
      : "";

    const convertedStores = (campaign.stores || []).map((store) => {
      const isAllStoresEntry =
        store.storeCode === STORE_CODE_VALUES.ALL_STORES_CODE;
      return {
        storeCode: store.storeCode,
        storeName: isAllStoresEntry ? "" : store.storeName || "",
        nickname: isAllStoresEntry
          ? ""
          : store.nickname || store.storeName || "",
      };
    });

    const isAllStores = campaign.allStoresSelected ?? (
      convertedStores.length === 1 &&
      convertedStores[0].storeCode === STORE_CODE_VALUES.ALL_STORES_CODE
    );

    const mechanic = campaign.campaignType ?? CampaignType.ROULETTE;

    reset({
      id: campaign.id,
      campaignName: campaign.nickname,
      slug: campaign.slug ?? "",
      campaignType: mechanic,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      stores: convertedStores,
      mechanicConfig: toFormMechanicConfig(campaign.mechanicConfig),

      faqItems: campaign.faqItems?.length
        ? campaign.faqItems
        : getDefaultFaqItems(mechanic),
      allStoresSelected: isAllStores,
    });
  }, [campaign, reset]);
};
